import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cloneDeep } from 'lodash';

import {
	Textarea,
	FormItem,
	Select,
	Button,
	Space,
	Modal,
	ModalTitle,
	setBlockItems,
	getBlockItems,
	ParticipantSelect,
	DayPickerInput,
} from '@components';

import './SummaryItemModal.scss';
import { Agenda, AgendaItem, ParticipantInfo, SummaryBlock, SummaryBlockItem } from '@api';

interface AddSummaryItemModalProps {
	closeModal: () => void,
	summaryBlocks: SummaryBlock[],
	summaryBlockIndex: number,
	summaryItemIndex: number,
	summaryAgendaItemId: string,
	agenda: Agenda,
	onSubmit: (updatedSummaryBlocks: SummaryBlock[]) => void,
}

function AddSummaryItemModal(props: AddSummaryItemModalProps): JSX.Element {
	const {
		closeModal,
		summaryBlocks,
		summaryBlockIndex,
		summaryItemIndex,
		summaryAgendaItemId,
		agenda,
		onSubmit,
	} = props;

	const
		[summaryItemText, setSummaryItemText] = useState<string>(''),
		[selectedSummaryBlock, setSelectedSummaryBlock] = useState<SummaryBlock>(null),
		[selectedSummaryAgendaItem, setSelectedSummaryAgendaItem] = useState<AgendaItem>(null);
		
		const [selectedPersons, setSelectedPersons] = useState<ParticipantInfo[]>([]);
		const [selectedDate, setSelectedDate] = useState(null as Date);

	const agendaItems = useMemo(() => {
		if (agenda) {
			const items: AgendaItem[] = cloneDeep(agenda.items);
			items.unshift(new AgendaItem({ id: null, text: '<Не задана>' }));
			return items;
		} else {
			return [new AgendaItem({ id: null, text: '<Не задана>' })];
		}
	}, [agenda]);

	useEffect(() => {
		const summaryBlock = summaryBlocks[summaryBlockIndex];
		setSummaryItemText('');
		setSelectedSummaryBlock(summaryBlock);
		setSelectedSummaryAgendaItem(agendaItems.filter(ai => ai.id == summaryAgendaItemId)[0]);

	}, [summaryBlocks, summaryBlockIndex, setSummaryItemText, setSelectedSummaryBlock,
		agendaItems, setSelectedSummaryAgendaItem, summaryAgendaItemId]);

	const handleChangeSummaryItemText = useCallback((e): void => {
		setSummaryItemText(e.target.value);
	}, [setSummaryItemText]);

	const handleChangeSelectedSummaryBlock = useCallback((selectedBlock: SummaryBlock): void => {
		setSelectedSummaryBlock(selectedBlock);
	}, [setSelectedSummaryBlock]);

	const handleChangeSelectedAgendaItem = useCallback((selectedAgendaItem: AgendaItem): void => {
		setSelectedSummaryAgendaItem(selectedAgendaItem);
	}, [setSelectedSummaryAgendaItem]);

	const handleSubmit = useCallback((): void => {
		const selectedSummaryBlockIndex = summaryBlocks.indexOf(selectedSummaryBlock);
		const updatedBlock: SummaryBlock = cloneDeep(summaryBlocks[selectedSummaryBlockIndex]);

		const selectedSummaryAgendaItemId = agenda && agenda.showInSummary && updatedBlock.showAgenda
			? selectedSummaryAgendaItem.id : null;

		const summaryItem = new SummaryBlockItem({
			text: summaryItemText,
			agendaItemId: selectedSummaryAgendaItemId,
			selectedDate: selectedDate,
			selectedPersons: selectedPersons,
		});
		
		const items = getBlockItems(updatedBlock.__data, selectedSummaryAgendaItemId);
		if (selectedSummaryBlockIndex == summaryBlockIndex) {
			items.splice(summaryItemIndex, 0, summaryItem);
		} else {
			items.push(summaryItem);
		}
		setBlockItems(updatedBlock.__data, selectedSummaryAgendaItemId, items);
		updatedBlock.items = updatedBlock.__data.getAllItems();
		summaryBlocks[selectedSummaryBlockIndex] = updatedBlock;
		
		onSubmit(summaryBlocks);		
		closeModal();

	}, [summaryBlocks, selectedSummaryBlock, summaryBlockIndex, summaryItemIndex,
		selectedDate, selectedPersons,
		agenda, summaryItemText, closeModal, onSubmit, selectedSummaryAgendaItem]);

	const headerView = useMemo((): JSX.Element => {
		return (
			<div className="resume-modal__header">
				<ModalTitle onClose={closeModal}>
					Добавление итога
				</ModalTitle>
			</div>
		);
	}, [closeModal]);

	const footerView = useMemo(() => {
		return (
			<Space>
				<Button theme="primary" onClick={handleSubmit}>
					Добавить
				</Button>
				<Button onClick={closeModal}>
					Отменить
				</Button>
			</Space>
		);
	}, [closeModal, handleSubmit]);

	const showAgendaItems = useMemo(() => {
		return agenda && agenda.showInSummary && selectedSummaryBlock && selectedSummaryBlock.showAgenda;
	}, [agenda, selectedSummaryBlock]);

	return selectedSummaryBlock && (
		<Modal
			className="resume-modal"
			header={headerView}
			footer={footerView}
			isOpen
		>
			<div className="resume-modal__content">
				<FormItem label="Раздел протокола">
				<Select<SummaryBlock>
						onChange={handleChangeSelectedSummaryBlock}
						textName="blockName"
						value={selectedSummaryBlock}
						options={summaryBlocks}
					/>
				</FormItem>
				<FormItem>
				<Textarea
						autoFocus
						className="resume-modal__textfield"
						value={summaryItemText}
						placeholder="Текст высказывания"
						onChange={handleChangeSummaryItemText}
					/>
				</FormItem>
				<Space gap="16px">
					<FormItem label="Исполнитель" contentClassName='person_box'>
						<ParticipantSelect
							suggestedValues={[]}
							values={selectedPersons}
							onChange={setSelectedPersons} 
							maxValues={1}
						/>
					</FormItem>
					<FormItem label="Срок" contentClassName='date_box'>
						<DayPickerInput
								value={selectedDate}
								onDayChange={setSelectedDate}
							/>
					</FormItem>
				</Space>
				{showAgendaItems &&
					<FormItem label="Повестка">
						<Select<AgendaItem>
							onChange={handleChangeSelectedAgendaItem}
							textName="text"
							value={selectedSummaryAgendaItem}
							options={agendaItems}
						/>
					</FormItem>
				}
			</div>
		</Modal>
	);
}

export {AddSummaryItemModal};